import {
  ArrowRightOnRectangleIcon,
  CreditCardIcon,
  DocumentTextIcon,
  HomeIcon,
  UserCircleIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import React from 'react';
import { Link, useLocation } from 'react-router-dom';

import UserAvatar from './UserAvatar';
import logo from '../../assets/HDLCC.avif';
import { useAuth } from '../../contexts/AuthContext';
import { useMember } from '../../contexts/MemberContext';

export default function Sidebar() {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { memberships } = useMember();

  // Check if user has multiple memberships
  const hasMultipleMemberships = memberships.length > 1;

  const menuItems = [
    {
      name: 'Dashboard',
      icon: HomeIcon,
      path: '/dashboard',
    },
    {
      name: 'My Profile',
      icon: UserCircleIcon,
      path: '/profile',
    },
    {
      name: 'Members Magazine',
      icon: DocumentTextIcon,
      path: '/magazine',
    },
    {
      name: 'Billing',
      icon: CreditCardIcon,
      path: '/billing',
    },
    ...(hasMultipleMemberships
      ? [
          {
            name: 'Manage Others',
            icon: UserGroupIcon,
            path: '/manage-others',
          },
        ]
      : []),
  ];

  const isActivePath = path => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="w-64 bg-hdlcc-darkGray border-r border-hdlcc-mediumGray/30 flex flex-col fixed h-screen shadow-elevation-2">
      <div className="p-5 border-b border-hdlcc-mediumGray/30 bg-gradient-to-br from-hdlcc-deepBlack via-hdlcc-black to-hdlcc-darkGray">
        <Link to="/dashboard" className="flex items-center space-x-3 group">
          <img
            src={logo}
            alt="HDLCC Logo"
            className="h-11 transition-transform group-hover:scale-105"
          />
          <div className="text-white">
            <h1 className="text-lg font-bold tracking-tight">HDLCC</h1>
            <p className="text-xs text-hdlcc-textSecondary">Member Portal</p>
          </div>
        </Link>
      </div>

      <div className="flex-1 p-3 overflow-y-auto">
        <nav>
          <ul className="space-y-1">
            {menuItems.map(item => (
              <li key={item.name}>
                <Link
                  to={item.path}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200 group ${
                    isActivePath(item.path)
                      ? 'bg-gradient-to-r from-hdlcc-primary/20 to-hdlcc-primary/10 text-hdlcc-primary border border-hdlcc-primary/30 shadow-glow-primary'
                      : 'text-hdlcc-textSecondary hover:bg-hdlcc-mediumGray hover:text-hdlcc-textPrimary border border-transparent'
                  }`}
                >
                  <item.icon
                    className={`w-5 h-5 transition-transform group-hover:scale-110 ${
                      isActivePath(item.path) ? 'text-hdlcc-primary' : ''
                    }`}
                  />
                  <span className="font-medium">{item.name}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </div>

      <div className="p-4 border-t border-hdlcc-mediumGray/30 bg-hdlcc-black/50">
        <div className="flex items-center space-x-3 px-3 py-2 rounded-lg bg-hdlcc-mediumGray/50 mb-2">
          <UserAvatar email={user?.email} size={40} />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-hdlcc-textPrimary truncate">
              {user?.name || 'Member Name'}
            </div>
            <div className="text-xs text-hdlcc-textTertiary truncate">{user?.email}</div>
          </div>
        </div>
        <button
          onClick={logout}
          className="w-full flex items-center justify-center space-x-2 px-4 py-2.5 text-hdlcc-textSecondary hover:text-hdlcc-error hover:bg-hdlcc-mediumGray rounded-lg transition-all duration-200 group border border-transparent hover:border-hdlcc-error/30"
        >
          <ArrowRightOnRectangleIcon className="w-5 h-5 transition-transform group-hover:translate-x-0.5" />
          <span className="font-medium">Sign Out</span>
        </button>
      </div>
    </div>
  );
}
